import { backend, domain } from 'modules/data';
import { z } from 'zod';

import { XeroContext } from '../../../data/xero/XeroContext';
import { excludeEmptyPersons } from '../../../selectors/xero';
import { addressSchema, phoneSchema, schema } from './XeroContactRequestForm.schema';

const mapTrackingCategoryTransfer = (
    categories: [string | null | undefined, string | null | undefined],
    trackingCategories: XeroContext['trackingCategories']
): backend.Transfer<backend.XeroDomainTrackingCategory>[] => {
    return categories.reduce<backend.Transfer<backend.XeroDomainTrackingCategory>[]>((acc, category, index) => {
        if (category) {
            const categoryData = trackingCategories[index];

            if (!categoryData) {
                return acc;
            }

            const categoryValue = categoryData.options.find((option) => option.id === category);

            acc.push({
                Name: categoryData.category.text,
                Option: categoryValue?.text,
            });
        }

        return acc;
    }, []);
};

export const mapFormToTransfer = (
    values: z.infer<typeof schema>,
    trackingCategories: XeroContext['trackingCategories']
): backend.transfers.RequestEditTransfer['xeroContactDetails'] => {
    return {
        accountNumber: values.accountNumber,
        name: values.name,
        firstName: values.primaryPersonFirstName,
        lastName: values.primaryPersonLastName,
        emailAddress: values.primaryPersonEmail,
        persons: (values.persons ?? []).filter(excludeEmptyPersons),

        phoneNumber: values.phone,
        fax: values.fax,
        mobile: values.mobile,
        directDial: values.directDial,
        billingAddress: values.billingAddress,
        deliveryAddress: values.deliveryAddress,
        taxNumber: values.taxNumber,
        companyNumber: values.companyNumber,
        salesAccountId: values.salesSettings.defaultAccount,
        salesTaxCode: values.salesTax,
        purchaseTaxCode: values.purchaseTax,
        purchasesAccountId: values.purchaseSettings.defaultAccount,
        salesTrackingCategories: mapTrackingCategoryTransfer(
            [values.salesSettings.trackingCategory1, values.salesSettings.trackingCategory2],
            trackingCategories
        ),
        purchasesTrackingCategories: mapTrackingCategoryTransfer(
            [values.purchaseSettings.trackingCategory1, values.purchaseSettings.trackingCategory2],
            trackingCategories
        ),
        defaultCurrency: values.defaultCurrency,
        bankAccountDetails: values.bankAccount?.sortCode
            ? `${values.bankAccount.sortCode}${values.bankAccount.accountNumber}`
            : values.bankAccount?.accountNumber,
        skypeName: values.skypeName,
    };
};

const mapPhone = (phone: domain.EditableXeroContactPhone | undefined): z.infer<typeof phoneSchema> => {
    return {
        countryCode: phone?.countryCode ?? '',
        areaCode: phone?.areaCode ?? '',
        number: phone?.number ?? '',
    };
};

const mapAddress = (address: domain.EditableXeroContactAddress | undefined): z.infer<typeof addressSchema> => {
    return {
        attentionTo: address?.attentionTo ?? '',
        address: address?.address ?? '',
        city: address?.city ?? '',
        region: address?.region ?? '',
        postalCode: address?.postalCode ?? '',
        country: address?.country ?? '',
    };
};

const mapDeliveryAddress = (
    address: domain.XeroStreetAddress | undefined
): z.infer<typeof schema>['deliveryAddress'] => {
    return {
        ...mapAddress(address),
        sameAsBillingAddress: address?.sameAsBillingAddress ?? false,
    };
};

export const mapRequestToForm = (
    details: domain.XeroContactRequest['details'],
    isUKCompany: boolean
): z.infer<typeof schema> => {
    const isBankAccountWithSortCode =
        isUKCompany && details.bankAccountDetails && /^\d{14}$/.test(details.bankAccountDetails);

    return {
        name: details.name ?? '',
        accountNumber: details.accountNumber ?? '',
        primaryPersonFirstName: details.firstName ?? '',
        primaryPersonLastName: details.lastName ?? '',
        primaryPersonEmail: details.emailAddress ?? '',
        persons: details.persons.map((person) => ({
            firstName: person.firstName ?? '',
            lastName: person.lastName ?? '',
            emailAddress: person.emailAddress ?? '',
        })),
        phone: mapPhone(details.phoneNumber),
        fax: mapPhone(details.fax),
        mobile: mapPhone(details.mobile),
        directDial: mapPhone(details.directDial),
        skypeName: details.skypeName ?? '',
        billingAddress: mapAddress(details.billingAddress),
        deliveryAddress: mapDeliveryAddress(details.deliveryAddress),
        salesSettings: {
            defaultAccount: details.salesAccount?.id ?? undefined,
            trackingCategory1: details.salesTrackingCategories[0]?.value?.id ?? undefined,
            trackingCategory2: details.salesTrackingCategories[1]?.value?.id ?? undefined,
        },
        purchaseSettings: {
            defaultAccount: details.purchasesAccount?.id ?? undefined,
            trackingCategory1: details.purchasesTrackingCategories[0]?.value?.id ?? undefined,
            trackingCategory2: details.purchasesTrackingCategories[1]?.value?.id ?? undefined,
        },
        taxNumber: details.taxNumber ?? '',
        companyNumber: details.companyNumber ?? '',
        salesTax: details.salesTax?.id ?? undefined,
        purchaseTax: details.purchaseTax?.id ?? undefined,
        defaultCurrency: details.defaultCurrency ?? undefined,
        bankAccount: {
            sortCode: isBankAccountWithSortCode ? details.bankAccountDetails?.slice(0, 6) : undefined,
            accountNumber: isBankAccountWithSortCode
                ? details.bankAccountDetails?.slice(6)
                : details.bankAccountDetails ?? '',
        },
    };
};
